import { useMemo, useState } from 'react';
import { Button, Checkbox, Input, JsonContent, Label, Modal, Select } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useMeSpaces } from '@/hooks/useMeSpaces';
import { useAwsRoleTrustPolicy, useStoreAwsRole } from '@/hooks/useIdentityCredentials';
import { AWS_SESSION_TAG_HELP, AgentPicker, AwsRoleTestResult, NONE } from './awsRoleShared';
import type { AwsRoleStoreResponse, ProviderSummary } from '@/types/identity';
import type { Scope } from './scope';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  provider: ProviderSummary;
  /** Space or org — IAM roles can't be personal. */
  scope: Scope;
}

const ROLE_ARN_RE = /^arn:aws[a-z-]*:iam::\d{12}:role\/[\w+=,.@/-]+$/;

/**
 * Register an AWS IAM role on a shared space or the org.
 *
 * Step 1 — policy builder: the account owner picks which `optr-*` session-tag
 * conditions to require (org always; this space; specific agents) and copies
 * the server-rendered trust policy. Step 2 — the role ARN. Save runs a
 * simulated AssumeRole (as the first selected agent, if any); a rejection
 * still saves the role (status `error`) and shows AWS's message plus the
 * exact tags that were sent, so the owner can fix the policy and re-test.
 */
export default function AddAwsRoleModal({ isOpen, onClose, provider, scope }: Props) {
  const { success } = useToast();
  const store = useStoreAwsRole();
  const { data: spacesData } = useMeSpaces();

  const isSpace = scope.kind === 'space';
  const sharedSpaces = useMemo(
    () => (spacesData?.items ?? []).filter((s) => s.kind !== 'personal'),
    [spacesData],
  );

  // Space scope: the credential's space, optionally pinned. Org scope: an
  // optional space to pin (org roles tag the space the agent runs in).
  const [pinSpace, setPinSpace] = useState(isSpace);
  const [orgSpaceId, setOrgSpaceId] = useState<string>(NONE);
  const [pinAgents, setPinAgents] = useState(false);
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [roleArn, setRoleArn] = useState('');
  const [accountName, setAccountName] = useState('');
  const [result, setResult] = useState<AwsRoleStoreResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const policySpaceId = isSpace
    ? pinSpace
      ? scope.spaceId
      : undefined
    : orgSpaceId !== NONE
      ? orgSpaceId
      : undefined;
  // Agents are listed from the pinned space (space scope: always its space).
  const agentSpaceId = isSpace ? scope.spaceId : policySpaceId;
  const effectiveAgentIds = pinAgents ? agentIds : [];

  const policy = useAwsRoleTrustPolicy({
    spaceId: policySpaceId,
    agentIds: effectiveAgentIds,
    enabled: isOpen && scope.kind !== 'user',
  });

  const handleClose = () => {
    setResult(null);
    setSubmitError(null);
    onClose();
  };

  const arnValid = ROLE_ARN_RE.test(roleArn.trim());

  const handleSubmit = async () => {
    if (scope.kind === 'user') return;
    setSubmitError(null);
    const test = {
      ...(effectiveAgentIds[0] && { agentId: effectiveAgentIds[0] }),
      ...(!isSpace && policySpaceId && { spaceId: policySpaceId }),
    };
    const body = {
      roleArn: roleArn.trim(),
      ...(accountName.trim() && { accountName: accountName.trim() }),
      ...(Object.keys(test).length > 0 && { test }),
    };
    try {
      const res = await store.mutateAsync(
        scope.kind === 'org' ? { scope: 'org', body } : { scope: { spaceId: scope.spaceId }, body },
      );
      if (res.test.ok) {
        success(`${provider.displayName} role connected`);
        handleClose();
      } else {
        // Saved with status `error` — keep the modal open with the diagnosis.
        setResult(res);
      }
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Could not save the IAM role');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add ${provider.displayName}`}
      size="lg"
      footer={
        result ? (
          <Button onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button variant="outline" onClick={handleClose} disabled={store.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!arnValid} isLoading={store.isPending}>
              Save and test
            </Button>
          </>
        )
      }
    >
      <div className="space-y-5">
        <section className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground">1. Trust policy for the role owner</h4>
          <p className="text-sm text-muted-foreground">
            In the target AWS account, create (or edit) the IAM role and set this trust policy. OPTR
            assumes the role through its broker role with <code>ExternalId</code> = your org id and
            the session tags below, so the account owner decides which org, space or agents may use it.
          </p>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox id="pin-org" checked disabled />
              <Label htmlFor="pin-org" className="text-sm font-normal">
                Restrict to this organization (always)
              </Label>
            </div>
            {isSpace ? (
              <div className="flex items-center gap-2">
                <Checkbox id="pin-space" checked={pinSpace} onCheckedChange={(v) => setPinSpace(!!v)} />
                <Label htmlFor="pin-space" className="text-sm font-normal">
                  Restrict to this space ({scope.spaceName})
                </Label>
              </div>
            ) : (
              <Select
                label="Restrict to a space (optional)"
                value={orgSpaceId}
                onChange={(e: { target: { value: string } }) => {
                  setOrgSpaceId(e.target.value);
                  setAgentIds([]);
                }}
                options={[
                  { value: NONE, label: 'Any space in this organization' },
                  ...sharedSpaces.map((s) => ({ value: s.spaceId, label: s.name })),
                ]}
              />
            )}
            <div className="flex items-center gap-2">
              <Checkbox id="pin-agents" checked={pinAgents} onCheckedChange={(v) => setPinAgents(!!v)} />
              <Label htmlFor="pin-agents" className="text-sm font-normal">
                Restrict to specific agents
              </Label>
            </div>
            {pinAgents && <AgentPicker spaceId={agentSpaceId} selected={agentIds} onChange={setAgentIds} />}
          </div>

          {policy.isLoading && <p className="text-xs text-muted-foreground">Rendering trust policy…</p>}
          {policy.error && (
            <p className="text-sm text-status-error-fg">
              {policy.error instanceof Error ? policy.error.message : 'Could not load the trust policy'}
            </p>
          )}
          {policy.data && (
            <>
              <JsonContent maxHeight="18rem">{JSON.stringify(policy.data.trustPolicy)}</JsonContent>
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Session tags & fine-grained permissions</summary>
                <ul className="mt-2 space-y-1">
                  {AWS_SESSION_TAG_HELP.map((t) => (
                    <li key={t.key}>
                      <code>{t.key}</code> — {t.description}
                    </li>
                  ))}
                </ul>
                <p className="mt-2">
                  Tags are also available as <code>aws:PrincipalTag/optr-*</code> in the role&apos;s
                  permission policies (ABAC), e.g.:
                </p>
                <JsonContent showHeader={false}>{JSON.stringify(policy.data.abacExample)}</JsonContent>
                <p className="mt-2">
                  Personal AWS IAM Identity Center logins cannot carry these tags; they only apply to
                  roles registered here.
                </p>
              </details>
            </>
          )}
        </section>

        <section className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground">2. The role</h4>
          <div className="space-y-1.5">
            <Label htmlFor="roleArn">Role ARN</Label>
            <Input
              id="roleArn"
              value={roleArn}
              onChange={(e) => setRoleArn(e.target.value)}
              placeholder="arn:aws:iam::123456789012:role/optr-readonly"
              autoComplete="off"
            />
            {roleArn.trim() && !arnValid && (
              <p className="text-xs text-status-error-fg">Enter an IAM role ARN.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="accountName">Account label (optional)</Label>
            <Input
              id="accountName"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="e.g. prod-analytics"
            />
            <p className="text-xs text-muted-foreground">
              Shown to agents in <code>aws_list_accounts</code>. Defaults to the account id.
            </p>
          </div>
          {pinAgents && effectiveAgentIds.length > 0 && (
            <p className="text-xs text-muted-foreground">
              The save-time test runs as the first selected agent.
            </p>
          )}
        </section>

        {result && (
          <div className="space-y-2">
            <p className="text-sm text-foreground">
              The role was saved, but OPTR could not assume it yet. Update the trust policy, then use
              <em> Test as agent…</em> on the role.
            </p>
            <AwsRoleTestResult
              ok={false}
              reason={result.test.reason}
              message={result.test.message}
              simulatedTags={result.test.simulatedTags}
            />
          </div>
        )}
        {submitError && (
          <p className="text-sm text-status-error-fg" role="alert">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
}
